import React, { useEffect, useMemo, useState } from 'react';
import { normalizeRelation } from '../../Utils/normalizeRelation';
import { calculateAge } from '../../Utils/family-tree/dateAndTime';
import { isDateComplete } from '../../Utils/dateAndTime';
import {
    AddMemberAutoInput,
    AddMemberAutoRelationField,
    calcYearsDeceased,
} from './descendantSheetHelpers';
import { getEmailFormatError } from '../../Utils/family-tree/email';
import { getPhoneInputValue, phoneInputCountrySearchProps } from '../../Utils/phone';
import { AddMemberSpecialNotesField } from './AddMemberSpecialNotesField';
import {
    ADD_MEMBER_DATE_FORMAT,
    ADD_MEMBER_FIELD_LIMITS,
} from '../../Utils/family-tree/addMemberFieldLimits';
import {
    getPrimaryFallbackDob,
    isEditModeMinor,
    isEmailRequiredForMember,
} from '../../Utils/family-tree/memberAge';

export function MainMemberSection({
    toggleSection,
    primaryLabel,
    openSection,
    uploadedImages,
    imageValidationError,
    removeImage,
    handleFileChange,
    sortedGenderOptions,
    grandfatherData,
    handleGrandfatherChange,
    setFieldValidationErrors,
    fieldValidationErrors,
    touchedFields,
    setTouchedFields,
    formSubmitted,
    isRootGeneration,
    rootHeadData,
    handleAddAnotherMarriage,
    CustomDatePicker,
    dateValidationErrors,
    sortedStates,
    sortedMaritalStatusOptions,
    sortedLivingDeceasedOptions,
    multipleBirthsOptions,
    sortedCellTypeOptions,
    emailCheckTimeoutRef,
    checkEmailExists,
    handlePhoneChange,
    PhoneInput,
    isEditMode,
    parentMember,
    editMemberData,
    setGrandfatherData,
    primaryRelation,
    primaryRelationName,
    DropdownSelect,
    currentGeneration,
    parentChildren = []
}) {
    console.log("relations in main member : ",primaryRelation,primaryRelationName)

    const primaryFallbackDob = useMemo(
        () => getPrimaryFallbackDob(editMemberData),
        [editMemberData]
    );
    const isPrimaryMinor = useMemo(
        () =>
            isEditModeMinor({
                isEditMode,
                dob: grandfatherData.dob,
                currentAge: grandfatherData.currentAge,
                fallbackDob: primaryFallbackDob,
            }),
        [
            isEditMode,
            grandfatherData.dob,
            grandfatherData.currentAge,
            primaryFallbackDob,
        ]
    );
    const isPrimaryEmailRequired = useMemo(
        () =>
            isEmailRequiredForMember({
                isEditMode,
                isDeceased: grandfatherData.aliveStatus === "2",
                dob: grandfatherData.dob,
                currentAge: grandfatherData.currentAge,
                fallbackDob: primaryFallbackDob,
            }),
        [
            isEditMode,
            grandfatherData.aliveStatus,
            grandfatherData.dob,
            grandfatherData.currentAge,
            primaryFallbackDob,
        ]
    );
    
    // Check if we're in generation 2
    const isGeneration2 = currentGeneration === 2;
    
    // State for accordion (sibling order selection)
    const [isSiblingOrderAccordionOpen, setIsSiblingOrderAccordionOpen] = useState(false);
    
    // Memoize parentChildren to prevent infinite loops - create stable reference
    const memoizedParentChildren = useMemo(() => {
        return Array.isArray(parentChildren) ? parentChildren : [];
    }, [parentChildren?.length, parentChildren?.map(c => c?.id).join(',')]);
    
    // Helper function to extract first name from child object
    const getChildFirstName = (child) => {
        // First try first_name field
        if (child.first_name) {
            return child.first_name.trim();
        }
        // If not available, try name field and extract first word (before space)
        if (child.name) {
            return child.name.trim().split(' ')[0];
        }
        return "";
    };
    
    // Helper function to get suffix from child object
    const getChildSuffix = (child) => {
        // First try suffix field directly
        if (child.suffix) {
            return child.suffix.trim().toLowerCase();
        }
        // If suffix field is not available, try to extract from name field
        // Suffix is typically the last part after the last space (e.g., "John Smith Jr" -> "Jr")
        if (child.name) {
            const nameParts = child.name.trim().split(' ');
            // Common suffixes: Jr, Sr, II, III, IV, etc.
            const commonSuffixes = ['jr', 'sr', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
            if (nameParts.length > 1) {
                const lastPart = nameParts[nameParts.length - 1].toLowerCase();
                // Check if last part is a common suffix
                if (commonSuffixes.includes(lastPart) || /^(i{1,3}|iv|v|vi{0,3}|ix|x)$/i.test(lastPart)) {
                    return lastPart;
                }
            }
        }
        return "";
    };
    
    // Log COMPLETE sibling data for the current generation
    useEffect(() => {
        console.log("🔵 [MainMemberSection] COMPLETE SIBLING DATA FOR GENERATION:", {
            currentGeneration: currentGeneration,
            isGeneration2: isGeneration2,
            isEditMode: isEditMode,
            siblingCount: memoizedParentChildren.length,
            allSiblings: memoizedParentChildren.map((sibling, index) => {
                // Log ALL available fields from each sibling
                return {
                    index: index,
                    // All keys available in sibling object
                    allKeys: Object.keys(sibling),
                    // Complete sibling object
                    completeSiblingData: sibling,
                    // Common fields (for easy reference)
                    id: sibling.id,
                    user_id: sibling.user_id || sibling.userId || sibling.User?.id || sibling.User?.user_id,
                    first_name: sibling.first_name,
                    last_name: sibling.last_name,
                    name: sibling.name,
                    full_name: sibling.full_name,
                    suffix: sibling.suffix,
                    dob: sibling.dob,
                    gender: sibling.gender,
                    generation_level: sibling.generation_level || sibling.generation,
                    multiple_births_type: sibling.multiple_births_type,
                    multiple_birth_order_number: sibling.multiple_birth_order_number,
                    multiple_birth_member_user_id: sibling.multiple_birth_member_user_id,
                    present_or_deceased: sibling.present_or_deceased,
                    aliveStatus: sibling.aliveStatus,
                    marital_status: sibling.marital_status,
                    email: sibling.email,
                    phone: sibling.phone,
                    relation: sibling.relation,
                    parent_id: sibling.parent_id || sibling.parentId || sibling.related_member_id,
                    // Nested objects
                    User: sibling.User,
                    parent: sibling.parent,
                    // Any other fields
                    ...sibling
                };
            })
        });
    }, [currentGeneration, isGeneration2, isEditMode, memoizedParentChildren]);
    
    // Check if firstName matches any child's first_name AND suffixes match (or both empty)
    // Special initial is required if: same first name AND (both have no suffix OR both have same suffix)
    const firstNameMatches = useMemo(() => {
        if (!isGeneration2 || !grandfatherData.firstName || memoizedParentChildren.length === 0) {
            return false;
        }
        const enteredFirstName = grandfatherData.firstName.trim().toLowerCase();
        const enteredSuffix = (grandfatherData.suffix || "").trim().toLowerCase();
        
        const matchResult = memoizedParentChildren.some(child => {
            const childFirstName = getChildFirstName(child).toLowerCase();
            const childSuffix = getChildSuffix(child);
            
            // In edit mode, exclude the current member being edited
            if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
                return false;
            }
            
            // Check if first names match
            if (childFirstName !== enteredFirstName || childFirstName === "") {
                return false;
            }
            
            // If first names match, check suffixes:
            // - Both have no suffix (empty) → require special initial
            // - Both have same suffix → require special initial
            // - Different suffixes → don't require special initial
            const bothNoSuffix = enteredSuffix === "" && childSuffix === "";
            const bothSameSuffix = enteredSuffix !== "" && childSuffix !== "" && enteredSuffix === childSuffix;
            
            // Log comparison for debugging
            if (childFirstName === enteredFirstName) {
                console.log("🔍 [MainMemberSection] Comparing:", {
                    enteredFirstName,
                    enteredSuffix,
                    childFirstName,
                    childSuffix,
                    bothNoSuffix,
                    bothSameSuffix,
                    requiresSpecialInitial: bothNoSuffix || bothSameSuffix
                });
            }
            
            return bothNoSuffix || bothSameSuffix;
        });
        
        return matchResult;
    }, [isGeneration2, grandfatherData.firstName, grandfatherData.suffix, memoizedParentChildren, isEditMode, editMemberData?.id]);
    
    // Special initial is required if firstName matches any child
    const isSpecialInitialRequired = firstNameMatches;
    
    // Log COMPLETE sibling data for the current generation
    useEffect(() => {
        console.log("🔵 [MainMemberSection] COMPLETE SIBLING DATA FOR GENERATION:", {
            currentGeneration: currentGeneration,
            isGeneration2: isGeneration2,
            isEditMode: isEditMode,
            siblingCount: memoizedParentChildren.length,
            allSiblings: memoizedParentChildren.map((sibling, index) => {
                // Log ALL available fields from each sibling
                return {
                    index: index,
                    // All keys available in sibling object
                    allKeys: Object.keys(sibling),
                    // Complete sibling object
                    completeSiblingData: sibling,
                    // Common fields (for easy reference)
                    id: sibling.id,
                    user_id: sibling.user_id || sibling.userId || sibling.User?.id || sibling.User?.user_id,
                    first_name: sibling.first_name,
                    last_name: sibling.last_name,
                    name: sibling.name,
                    full_name: sibling.full_name,
                    suffix: sibling.suffix,
                    dob: sibling.dob,
                    gender: sibling.gender,
                    generation_level: sibling.generation_level || sibling.generation,
                    multiple_births_type: sibling.multiple_births_type,
                    multiple_birth_order_number: sibling.multiple_birth_order_number,
                    multiple_birth_member_user_id: sibling.multiple_birth_member_user_id,
                    present_or_deceased: sibling.present_or_deceased,
                    aliveStatus: sibling.aliveStatus,
                    marital_status: sibling.marital_status,
                    email: sibling.email,
                    phone: sibling.phone,
                    relation: sibling.relation,
                    parent_id: sibling.parent_id || sibling.parentId || sibling.related_member_id,
                    // Nested objects
                    User: sibling.User,
                    parent: sibling.parent,
                    // Any other fields
                    ...sibling
                };
            })
        });
    }, [currentGeneration, isGeneration2, isEditMode, memoizedParentChildren]);
    
    // Normalize DOB for comparison - extracts year, month, day and compares as date string (YYYY-MM-DD)
    // This avoids timezone issues by comparing dates at the day level
    const normalizeDobForComparison = (dob) => {
        if (!dob || dob === null || dob === undefined || dob === '') return null;
        
        let year, month, day;
        
        // Handle timestamps (numbers or numeric strings) - convert to date
        if (typeof dob === 'number' || (typeof dob === 'string' && /^-?\d+$/.test(dob.trim()))) {
            const timestamp = typeof dob === 'number' ? dob : parseInt(dob.trim(), 10);
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) {
                // Use local date methods to match how timestamps are typically stored (local midnight)
                // This ensures "01/05/1949" matches timestamp that represents Jan 5, 1949 in local time
                year = date.getFullYear();
                month = date.getMonth() + 1;
                day = date.getDate();
            } else {
                return null;
            }
        } else if (typeof dob === 'string') {
            const trimmed = dob.trim();
            
            // Handle YYYY/MM/DD format (from CustomDatePicker)
            const yyyyMMddMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
            if (yyyyMMddMatch) {
                [, year, month, day] = yyyyMMddMatch;
            }
            // Handle YYYY-MM-DD format
            else if (trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)) {
                const parts = trimmed.split('-');
                [year, month, day] = parts;
            }
            // Handle MM/DD/YYYY format
            else {
                const mmddyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                if (mmddyyyyMatch) {
                    [, month, day, year] = mmddyyyyMatch;
                }
                // Try to parse as a general date string
                else {
                    try {
                        const date = new Date(trimmed);
                        if (!isNaN(date.getTime())) {
                            // Use UTC to avoid timezone issues
                            year = date.getUTCFullYear();
                            month = date.getUTCMonth() + 1;
                            day = date.getUTCDate();
                        } else {
                            // Try manual parsing
                            const parts = trimmed.split(/[-\/]/);
                            if (parts.length === 3) {
                                if (parts[0].length === 4 && /^\d{4}$/.test(parts[0])) {
                                    [year, month, day] = parts;
                                } else {
                                    [month, day, year] = parts;
                                }
                            } else {
                                return null;
                            }
                        }
                    } catch (e) {
                        return null;
                    }
                }
            }
        }
        
        // Return normalized date string (YYYY-MM-DD) for comparison
        if (year && month && day) {
            const normalizedYear = String(parseInt(year, 10)).padStart(4, '0');
            const normalizedMonth = String(parseInt(month, 10)).padStart(2, '0');
            const normalizedDay = String(parseInt(day, 10)).padStart(2, '0');
            return `${normalizedYear}-${normalizedMonth}-${normalizedDay}`;
        }
        
        return null;
    };
    
    // Check if two DOBs are within ±rangeDays (for twin/triplet: same birth considered within 3 days)
    const isDobWithinRange = (dob1, dob2, rangeDays = 3) => {
        const n1 = normalizeDobForComparison(dob1);
        const n2 = normalizeDobForComparison(dob2);
        if (!n1 || !n2) return false;
        const [y1, m1, d1] = n1.split('-').map(Number);
        const [y2, m2, d2] = n2.split('-').map(Number);
        const date1 = new Date(y1, m1 - 1, d1);
        const date2 = new Date(y2, m2 - 1, d2);
        const msPerDay = 24 * 60 * 60 * 1000;
        const dayDiff = Math.round((date1.getTime() - date2.getTime()) / msPerDay);
        return Math.abs(dayDiff) <= rangeDays;
    };
    
    // Check if current member has multiple_births_type set (for edit mode)
    const currentMemberHasMultipleBirthsType = useMemo(() => {
        const multipleBirthsType = grandfatherData.multiple_births_type;
        const hasMultipleBirthsType = multipleBirthsType !== null && 
                                    multipleBirthsType !== undefined && 
                                    multipleBirthsType !== '' &&
                                    (
                                        (typeof multipleBirthsType === 'string' && /^[2-9]$/.test(multipleBirthsType.trim())) ||
                                        (typeof multipleBirthsType === 'number' && multipleBirthsType >= 2 && multipleBirthsType <= 9)
                                    );
        
        console.log("🔍 [MainMemberSection] Current Member Multiple Births Type Check:", {
            multipleBirthsType: multipleBirthsType,
            hasMultipleBirthsType: hasMultipleBirthsType
        });
        
        return hasMultipleBirthsType;
    }, [grandfatherData.multiple_births_type]);
    
    // Check if current DOB matches any sibling's DOB
    // Enable if DOB matches (both edit and add mode, regardless of generation level)
    const hasMatchingSiblingDob = useMemo(() => {
        // Check for DOB match in both edit and add mode, regardless of generation level
        if (!grandfatherData.dob || !isDateComplete(grandfatherData.dob) || memoizedParentChildren.length === 0) {
            console.log("🔍 [MainMemberSection] DOB Match Check - Early Return:", {
                isEditMode,
                hasDob: !!grandfatherData.dob,
                dobValue: grandfatherData.dob,
                isDateComplete: isDateComplete(grandfatherData.dob),
                siblingCount: memoizedParentChildren.length
            });
            return false;
        }
        
        const currentDobNormalized = normalizeDobForComparison(grandfatherData.dob);
        console.log("🔍 [MainMemberSection] DOB Normalization:", {
            originalDob: grandfatherData.dob,
            normalizedDob: currentDobNormalized,
            isDateComplete: isDateComplete(grandfatherData.dob)
        });
        
        if (!currentDobNormalized) {
            console.log("⚠️ [MainMemberSection] Failed to normalize current DOB");
            return false;
        }
        
        const matchResult = memoizedParentChildren.some(child => {
            // In edit mode, exclude the current member being edited
            if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
                console.log(`🔍 [MainMemberSection] Excluding current member from comparison:`, {
                    childId: child.id,
                    editMemberId: editMemberData.id,
                    childName: child.first_name || child.name
                });
                return false;
            }
            
            if (!child.dob) {
                console.log(`🔍 [MainMemberSection] Sibling ${child.id} has no DOB`);
                return false;
            }
            
            const siblingDobNormalized = normalizeDobForComparison(child.dob);
            // Twin/triplet: consider siblings within ±3 days as same multiple-birth set
            const isDobMatch = isDobWithinRange(grandfatherData.dob, child.dob, 3);
            
            console.log(`🔍 [MainMemberSection] Comparing DOBs (±3 day range):`, {
                isEditMode: isEditMode,
                siblingId: child.id,
                siblingName: child.first_name || child.name,
                siblingOriginalDob: child.dob,
                siblingNormalizedDob: siblingDobNormalized,
                currentNormalizedDob: currentDobNormalized,
                isDobMatch: isDobMatch,
                matchDetails: {
                    currentDobType: typeof grandfatherData.dob,
                    siblingDobType: typeof child.dob,
                    withinRange: isDobMatch
                }
            });
            
            if (!isDobMatch) {
                return false;
            }
            
            // Enable if DOB is within range of a sibling (both edit and add mode)
            // User can set multiple_births_type when DOB is within ±3 days of a sibling
            const shouldEnable = isDobMatch;
            
            if (shouldEnable) {
                console.log(`✅ [MainMemberSection] DOB match found! Enabling dropdown.`, {
                    mode: isEditMode ? 'edit' : 'add',
                    siblingName: child.first_name || child.name
                });
            }
            
            return shouldEnable;
        });
        
        console.log("🔍 [MainMemberSection] Final DOB Match Result:", {
            hasMatchingSiblingDob: matchResult,
            currentDob: grandfatherData.dob,
            siblingCount: memoizedParentChildren.length
        });
        
        return matchResult;
    }, [isEditMode, grandfatherData.dob, memoizedParentChildren, editMemberData?.id, currentMemberHasMultipleBirthsType]);
    
    // Enable Multiple Births dropdown if:
    // 1. Current member already has multiple_births_type set, OR
    // 2. DOB matches a sibling (enables in both edit and add mode)
    const shouldEnableMultipleBirths = currentMemberHasMultipleBirthsType || hasMatchingSiblingDob;
    
    // Get siblings whose DOB is within ±3 days (twin/triplet range) for multiple birth selection
    const siblingsWithSameDob = useMemo(() => {
        if (!grandfatherData.dob || !isDateComplete(grandfatherData.dob) || memoizedParentChildren.length === 0) {
            return [];
        }
        
        const currentDobNormalized = normalizeDobForComparison(grandfatherData.dob);
        if (!currentDobNormalized) {
            return [];
        }
        
        return memoizedParentChildren
            .filter(child => {
                // In edit mode, exclude the current member being edited
                if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
                    return false;
                }
                // Always show all siblings in DOB range so user can select/deselect; never hide an eligible sibling
                // (so unselected siblings still show with name and checkbox for re-selection)
                if (!child.dob) {
                    return false;
                }
                
                // Twin/triplet: within ±3 days counts as same multiple-birth set
                return isDobWithinRange(grandfatherData.dob, child.dob, 3);
            })
            .map(child => ({
                value: child.id?.toString() || "",
                label: `${child.first_name || child.name?.split(' ')[0] || 'Unknown'} ${child.last_name || child.name?.split(' ').slice(1).join(' ') || ''}`.trim(),
                id: child.id,
                memberData: child
            }));
    }, [grandfatherData.dob, grandfatherData.multiple_birth_details, memoizedParentChildren, isEditMode, editMemberData?.id]);

    const yearsDeceased = useMemo(
        () => calcYearsDeceased(grandfatherData.dod),
        [grandfatherData.dod]
    );
    
    // When adding a child (not edit): if DOB is within ±3 days of existing sibling(s), auto-select type by total count.
    // E.g. 2 existing twins + this child → Triplet (3) automatically; 3 existing + this → Quad (4); etc.
    // Total in set = existing siblings in range + current child being added
    useEffect(() => {
        if (isEditMode) return;
        if (!hasMatchingSiblingDob || siblingsWithSameDob.length === 0) return;
        const totalInSet = siblingsWithSameDob.length + 1; // existing in range + this child
        const suggestedType = Math.min(9, Math.max(2, totalInSet));
        const currentType = grandfatherData.multiple_births_type;
        const currentNum = currentType !== null && currentType !== undefined && currentType !== ''
            ? (typeof currentType === 'string' ? parseInt(currentType, 10) : currentType)
            : 0;
        // Always sync to suggested type so e.g. two twins become triplets when third child is added with same DOB
        if (currentNum !== suggestedType) {
            setGrandfatherData((prev) => ({
                ...prev,
                multiple_births_type: String(suggestedType),
            }));
        }
    }, [isEditMode, hasMatchingSiblingDob, siblingsWithSameDob.length, grandfatherData.multiple_births_type, setGrandfatherData]);
    
    // Edit mode: when DOB is changed so it's no longer within ±3 days of any sibling, clear multiple birth (unselect + disable)
    useEffect(() => {
        if (!isEditMode) return;
        if (hasMatchingSiblingDob) return;
        if (memoizedParentChildren.length === 0) return; // avoid clearing before siblings loaded
        const hasMultipleBirth = grandfatherData.multiple_births_type && (grandfatherData.multiple_birth_details?.length > 0 || grandfatherData.multiple_birth_order);
        if (!hasMultipleBirth) return;
        setGrandfatherData((prev) => ({
            ...prev,
            multiple_births_type: "",
            multiple_birth_details: [],
            multiple_birth_order: "",
        }));
    }, [isEditMode, hasMatchingSiblingDob, memoizedParentChildren.length, grandfatherData.multiple_births_type, grandfatherData.multiple_birth_details, grandfatherData.multiple_birth_order, setGrandfatherData]);
    
    // Get the required number of siblings based on multiple_births_type
    const requiredSiblingCount = useMemo(() => {
        const multipleBirthsType = grandfatherData.multiple_births_type;
        if (!multipleBirthsType) return null;
        
        const typeValue = typeof multipleBirthsType === 'string' 
            ? parseInt(multipleBirthsType, 10) 
            : multipleBirthsType;
        
        // For twin (2), user needs to select 1 sibling (the other twin)
        // For triplet (3), user needs to select 2 siblings (the other two)
        // So required count is typeValue - 1
        return typeValue >= 2 && typeValue <= 9 ? typeValue - 1 : null;
    }, [grandfatherData.multiple_births_type]);
    
    // Get current selected sibling IDs (excluding the current member if in edit mode)
    const selectedSiblingIds = useMemo(() => {
        const multipleBirthDetails = grandfatherData.multiple_birth_details || [];
        const currentMemberId = isEditMode && editMemberData?.id ? editMemberData.id.toString() : null;
        
        return multipleBirthDetails
            .filter(detail => {
                // In edit mode, exclude the current member's entry from the count
                if (currentMemberId && detail.family_member_id?.toString() === currentMemberId) {
                    return false;
                }
                return true;
            })
            .map(detail => detail.family_member_id?.toString() || "")
            .filter(Boolean);
    }, [grandfatherData.multiple_birth_details, isEditMode, editMemberData?.id]);
    
    // Generate order options based on multiple_births_type
    const orderOptions = useMemo(() => {
        const multipleBirthsType = grandfatherData.multiple_births_type;
        if (!multipleBirthsType) return [];
        
        const typeValue = typeof multipleBirthsType === 'string' 
            ? parseInt(multipleBirthsType, 10) 
            : multipleBirthsType;
        
        if (typeValue < 2 || typeValue > 9) return [];
        
        // Create options from 1 to typeValue
        return Array.from({ length: typeValue }, (_, i) => ({
            value: (i + 1).toString(),
            label: `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}`
        }));
    }, [grandfatherData.multiple_births_type]);
    
    // Validation function for multiple birth orders
    const validateMultipleBirthOrders = (details, maxOrder) => {
        if (!maxOrder || maxOrder < 2) return;
        
        // Filter out siblings without orders (just selected but order not assigned yet)
        const detailsWithOrders = details.filter(d => d.order !== "" && d.order !== null && d.order !== undefined);
        
        if (detailsWithOrders.length === 0) {
            // No orders assigned yet, clear error
            setFieldValidationErrors((prev) => ({
                ...prev,
                multipleBirthOrders: "",
            }));
            return;
        }
        
        const orders = detailsWithOrders
            .map(d => d.order)
            .map(order => typeof order === 'string' ? parseInt(order, 10) : order)
            .filter(order => !isNaN(order));
        
        // Check for duplicates
        const uniqueOrders = new Set(orders);
        if (orders.length !== uniqueOrders.size) {
            setFieldValidationErrors((prev) => ({
                ...prev,
                multipleBirthOrders: "Each sibling must have a unique birth order",
            }));
            return;
        }
        
        // Check if all orders are within valid range
        const invalidOrders = orders.filter(order => order < 1 || order > maxOrder);
        if (invalidOrders.length > 0) {
            setFieldValidationErrors((prev) => ({
                ...prev,
                multipleBirthOrders: `Birth orders must be between 1 and ${maxOrder}`,
            }));
            return;
        }
        
        // Clear error if validation passes
        setFieldValidationErrors((prev) => ({
            ...prev,
            multipleBirthOrders: "",
        }));
    };
    
    // Validation function including current member's order
    const validateMultipleBirthOrdersWithCurrentMember = (details, currentMemberOrder, maxOrder) => {
        if (!maxOrder || maxOrder < 2) return;
        
        const orders = details
            .map(d => d.order)
            .filter(order => order !== "" && order !== null && order !== undefined)
            .map(order => parseInt(order, 10))
            .filter(order => !isNaN(order));
        
        // Add current member's order if provided
        if (currentMemberOrder && currentMemberOrder !== "" && !isNaN(parseInt(currentMemberOrder, 10))) {
            orders.push(parseInt(currentMemberOrder, 10));
        }
        
        // Check for duplicates
        const uniqueOrders = new Set(orders);
        if (orders.length !== uniqueOrders.size) {
            setFieldValidationErrors((prev) => ({
                ...prev,
                multipleBirthOrders: "Each member must have a unique birth order",
            }));
            return;
        }
        
        // Check if all orders are within valid range
        const invalidOrders = orders.filter(order => order < 1 || order > maxOrder);
        if (invalidOrders.length > 0) {
            setFieldValidationErrors((prev) => ({
                ...prev,
                multipleBirthOrders: `Birth orders must be between 1 and ${maxOrder}`,
            }));
            return;
        }
        
        // Check if all orders from 1 to maxOrder are assigned (when all fields are filled)
        const expectedCount = maxOrder; // Total number of members (siblings + current member)
        const filledOrders = orders.length;
        const allDetailsFilled = details.every(d => d.order && d.order !== "" && !isNaN(parseInt(d.order, 10)));
        const currentMemberFilled = currentMemberOrder && currentMemberOrder !== "" && !isNaN(parseInt(currentMemberOrder, 10));
        
        if (allDetailsFilled && currentMemberFilled) {
            // All fields are filled, check if we have all orders from 1 to maxOrder
            const sortedOrders = [...orders].sort((a, b) => a - b);
            const expectedOrders = Array.from({ length: maxOrder }, (_, i) => i + 1);
            const hasAllOrders = expectedOrders.every(order => sortedOrders.includes(order));
            
            if (!hasAllOrders) {
                setFieldValidationErrors((prev) => ({
                    ...prev,
                    multipleBirthOrders: `All birth orders from 1 to ${maxOrder} must be assigned`,
                }));
                return;
            }
        }
        
        // Clear error if validation passes
        setFieldValidationErrors((prev) => ({
            ...prev,
            multipleBirthOrders: "",
        }));
    };
    
    // useEffect to check for firstName matches and update validation in real-time
    useEffect(() => {
        if (isGeneration2 && grandfatherData.firstName && firstNameMatches && !grandfatherData.specialName?.trim()) {
            // Mark specialName as touched so error shows immediately
            setTouchedFields((prev) => {
                if (prev.primary.specialName) return prev; // Already touched, don't update
                return {
                    ...prev,
                    primary: {
                        ...prev.primary,
                        specialName: true,
                    },
                };
            });
            // Set error message
            setFieldValidationErrors((prev) => {
                if (prev.specialName === "Special initial is required when first name matches an existing child.") {
                    return prev; // Already set, don't update
                }
                return {
                    ...prev,
                    specialName: "Special initial is required when first name matches an existing child.",
                };
            });
        } else if (isGeneration2 && (!firstNameMatches || grandfatherData.specialName?.trim())) {
            // Clear error if no match or specialName is provided
            setFieldValidationErrors((prev) => {
                if (!prev.specialName) return prev; // Already cleared
                return {
                    ...prev,
                    specialName: "",
                };
            });
        } else if (!isGeneration2) {
            // Clear error if not generation 2
            setFieldValidationErrors((prev) => {
                if (!prev.specialName) return prev;
                return {
                    ...prev,
                    specialName: "",
                };
            });
        }
    }, [isGeneration2, grandfatherData.firstName, grandfatherData.specialName, firstNameMatches, setFieldValidationErrors, setTouchedFields]);
    
    // Validate multiple birth siblings on initial load or when data changes
    useEffect(() => {
        if (grandfatherData.multiple_births_type && requiredSiblingCount !== null) {
            const multipleBirthDetails = grandfatherData.multiple_birth_details || [];
            // Get current member ID - try multiple possible ID fields
            const currentMemberId = isEditMode && editMemberData ? (
                editMemberData.id?.toString() || 
                editMemberData.family_member_id?.toString() || 
                null
            ) : null;
            
            // Count siblings excluding current member
            // Use loose comparison to handle UUID strings, numbers, etc.
            const selectedSiblingsCount = multipleBirthDetails.filter(detail => {
                if (!currentMemberId) return true; // If no current member ID, include all
                
                const detailId = detail.family_member_id?.toString() || "";
                // Compare as strings, handling both UUIDs and numeric IDs
                if (detailId && currentMemberId && detailId === currentMemberId) {
                    return false; // Exclude current member
                }
                return true;
            }).length;
            
            // Debug logging (can be removed in production)
            if (isEditMode && currentMemberId) {
                console.log('🔍 [Multiple Birth Validation]', {
                    multipleBirthsType: grandfatherData.multiple_births_type,
                    requiredSiblingCount,
                    totalDetails: multipleBirthDetails.length,
                    selectedSiblingsCount,
                    currentMemberId,
                    detailIds: multipleBirthDetails.map(d => d.family_member_id?.toString()),
                    isValid: selectedSiblingsCount === requiredSiblingCount
                });
            }
            
            // In edit mode, always validate and clear errors if valid (data is already loaded)
            // In add mode, only show errors after field is touched or form submitted
            const shouldValidate = isEditMode || touchedFields.primary.multipleBirthSiblings || formSubmitted;
            
            setFieldValidationErrors((prev) => {
                if (selectedSiblingsCount === requiredSiblingCount) {
                    // Always clear error if validation passes (especially important in edit mode)
                    if (!prev.multipleBirthSiblings) return prev; // Already cleared
                    return {
                        ...prev,
                        multipleBirthSiblings: "",
                    };
                } else if (shouldValidate) {
                    // Set error if validation fails and we should validate
                    const errorMessage = `Please select exactly ${requiredSiblingCount} sibling${requiredSiblingCount > 1 ? 's' : ''} with the same DOB (or within ±3 days)`;
                    if (prev.multipleBirthSiblings === errorMessage) return prev; // Already set
                    return {
                        ...prev,
                        multipleBirthSiblings: errorMessage,
                    };
                }
                // Don't change error state if we shouldn't validate yet
                return prev;
            });
        } else if (!grandfatherData.multiple_births_type) {
            // Clear error if multiple births type is cleared
            setFieldValidationErrors((prev) => {
                if (!prev.multipleBirthSiblings) return prev;
                return {
                    ...prev,
                    multipleBirthSiblings: "",
                };
            });
        }
    }, [grandfatherData.multiple_births_type, grandfatherData.multiple_birth_details, requiredSiblingCount, isEditMode, editMemberData?.id, editMemberData?.family_member_id, touchedFields.primary.multipleBirthSiblings, formSubmitted, setFieldValidationErrors]);
    
    return (
        <React.Fragment>
            <div className="section addmember-section">
                <div
                    className="section-header addmember-section-header"
                    onClick={() => toggleSection("primary")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && toggleSection("primary")}
                >
                    <h3>Descendant Data</h3>
                    <span
                        className={`dropdown-icon ${openSection === "primary" ? "rotated" : ""
                            }`}
                    >
                        ▼
                    </span>
                </div>

                {openSection === "primary" && (
                    <React.Fragment>
                        {/* ... (Image upload primary JSX) ... */}
                        <div
                            className="image-row addmember-image-row "
                            style={{ display: "flex", alignItems: "center", gap: "10px" }}
                        >
                            {/* Default avatar when no images are uploaded */}
                            {uploadedImages.filter((img) => img.type == "main").length ===
                                0 && (
                                    <div
                                        style={{
                                            width: "65px",
                                            height: "65px",
                                            overflow: "hidden",
                                            border: imageValidationError
                                                ? "1px solid #ff4d4f"
                                                : "1px solid #E3E3E3",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <img
                                            src={window.getPublicUrl("/assets/images/d-user.jpg")}
                                            alt="Member"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    </div>
                                )}

                            {/* Display uploaded images */}
                            {uploadedImages
                                .filter((img) => img.type == "main")
                                .map((img, index) => (
                                    <div
                                        key={img.id}
                                        style={{
                                            width: "65px",
                                            height: "65px",
                                            borderRadius: "4px",
                                            overflow: "hidden",
                                            position: "relative",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <img
                                            src={img.url}
                                            alt={`Upload ${index + 1}`}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                            }}
                                        />
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeImage(img.id);
                                            }}
                                            style={{
                                                position: "absolute",
                                                top: "2px",
                                                right: "2px",
                                                background: "rgba(0,0,0,0.5)",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "50%",
                                                width: "18px",
                                                height: "18px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                padding: 0,
                                                lineHeight: 1,
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}

                            {/* Plus button - always show if less than 5 images */}
                            {uploadedImages.filter((img) => img.type == "main").length <
                                2 && (
                                    <div
                                        // onClick={() => fileInputRef.current?.click()}
                                        onClick={() =>
                                            document.getElementById("primary-image-input")?.click()
                                        }
                                        style={{
                                            cursor: "pointer",
                                            width: "65px",
                                            height: "65px",
                                            borderRadius: "4px",
                                            border: "1px dashed #ccc",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#f5f5f5",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <span style={{ fontSize: "24px", color: "#666" }}>+</span>
                                        <input
                                            id="primary-image-input"
                                            // ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={(e) => handleFileChange(e, "main")}
                                            multiple
                                        />
                                    </div>
                                )}
                        </div>

                        {imageValidationError && (
                            <div
                                style={{
                                    color: "#ff4d4f",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                }}
                            >
                                Profile image is required.
                            </div>
                        )}

                        <div className="row form-grid addmember-form-grid addmember-descendant-grid">
                            <div className="addmember-descendant-band addmember-descendant-band--names">
                            {/* Gender */}
                            <div className="form-group addmember-form-group addmember-field-gender">
                                <label className="required-label">Gender</label>
                                <DropdownSelect
                                    options={sortedGenderOptions}
                                    value={grandfatherData.gender}
                                    onChange={(val) => {
                                        handleGrandfatherChange({
                                            target: { name: "gender", value: val },
                                        });
                                        if (val) {
                                            setFieldValidationErrors((prev) => ({
                                                ...prev,
                                                gender: "",
                                            }));
                                        }
                                    }}
                                    placeholder=""
                                    maxMenuHeight={180}
                                    focusNextOnSelect
                                    maxSearchLength={ADD_MEMBER_FIELD_LIMITS.GENDER}
                                />
                                {
                                    fieldValidationErrors.gender && (
                                        <div id="gender-field"
                                            style={{
                                                color: "#d32f2f",
                                                fontSize: "12px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {fieldValidationErrors.gender}
                                        </div>
                                    )
                                }
                            </div>
                            
                            {/* First Name */}
                            <div className="form-group addmember-form-group">
                                <label
                                    htmlFor={`grandfather-firstName`}
                                    className={'required-labelsss'}
                                >
                                    First Name
                                    <span style={{ color: "red" }}> *</span>
                                </label>
                                <input
                                    id={`grandfather-firstName`}
                                    type="text"
                                    name="firstName"
                                    value={grandfatherData.firstName || ""}
                                    onChange={(e) => {
                                        handleGrandfatherChange(e);
                                        // For firstName field, check for matches immediately as user types
                                        if (isGeneration2) {
                                            const enteredFirstName = e.target.value.trim().toLowerCase();
                                            const enteredSuffix = (grandfatherData.suffix || "").trim().toLowerCase();
                                            const hasMatch = memoizedParentChildren.some(child => {
                                                const childFirstName = getChildFirstName(child).toLowerCase();
                                                const childSuffix = getChildSuffix(child);
                                                // In edit mode, exclude the current member being edited
                                                if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
                                                    return false;
                                                }
                                                // Check if first names match
                                                if (childFirstName !== enteredFirstName || childFirstName === "") {
                                                    return false;
                                                }
                                                // Check suffixes: require special initial if both no suffix or both same suffix
                                                const bothNoSuffix = enteredSuffix === "" && childSuffix === "";
                                                const bothSameSuffix = enteredSuffix !== "" && childSuffix !== "" && enteredSuffix === childSuffix;
                                                return bothNoSuffix || bothSameSuffix;
                                            });
                                            
                                            if (hasMatch) {
                                                // Mark specialName as touched so error shows immediately
                                                setTouchedFields((prev) => ({
                                                    ...prev,
                                                    primary: {
                                                        ...prev.primary,
                                                        specialName: true,
                                                    },
                                                }));
                                            }
                                        }
                                    }}
                                    placeholder=""
                                    onBlur={(e) => {
                                        // Trim the value when user leaves the field
                                        const trimmedValue = e.target.value.trim();
                                        if (trimmedValue !== e.target.value) {
                                            handleGrandfatherChange({
                                                target: { name: "firstName", value: trimmedValue },
                                            });
                                        }
                                        // Mark field as touched
                                        setTouchedFields((prev) => ({
                                            ...prev,
                                            primary: {
                                                ...prev.primary,
                                                firstName: true,
                                            },
                                        }));
                                        
                                        // Validate firstName to check for matches
                                        if (isGeneration2) {
                                            const enteredFirstName = trimmedValue.toLowerCase();
                                            const enteredSuffix = (grandfatherData.suffix || "").trim().toLowerCase();
                                            const hasMatch = memoizedParentChildren.some(child => {
                                                const childFirstName = getChildFirstName(child).toLowerCase();
                                                const childSuffix = getChildSuffix(child);
                                                // In edit mode, exclude the current member being edited
                                                if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
                                                    return false;
                                                }
                                                // Check if first names match
                                                if (childFirstName !== enteredFirstName || childFirstName === "") {
                                                    return false;
                                                }
                                                // Check suffixes: require special initial if both no suffix or both same suffix
                                                const bothNoSuffix = enteredSuffix === "" && childSuffix === "";
                                                const bothSameSuffix = enteredSuffix !== "" && childSuffix !== "" && enteredSuffix === childSuffix;
                                                return bothNoSuffix || bothSameSuffix;
                                            });
                                            
                                            if (hasMatch && !grandfatherData.specialName?.trim()) {
                                                // Mark specialName as touched so error shows immediately
                                                setTouchedFields((prev) => ({
                                                    ...prev,
                                                    primary: {
                                                        ...prev.primary,
                                                        specialName: true,
                                                    },
                                                }));
                                                setFieldValidationErrors((prev) => ({
                                                    ...prev,
                                                    specialName: "Special initial is required when first name matches an existing child.",
                                                }));
                                            } else if (!hasMatch) {
                                                setFieldValidationErrors((prev) => ({
                                                    ...prev,
                                                    specialName: "",
                                                }));
                                            }
                                        }
                                    }}
                                    required={true}
                                />
                                {fieldValidationErrors.firstName &&
                                    (touchedFields.primary.firstName || formSubmitted) && (
                                        <div
                                            style={{
                                                color: "#d32f2f",
                                                fontSize: "12px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {fieldValidationErrors.firstName}
                                        </div>
                                    )}
                            </div>

                            {/* Middle Name */}
                            <div className="form-group addmember-form-group">
                                <label
                                    htmlFor={`grandfather-middleName`}
                                    className={'required-labelsss'}
                                >
                                    Middle Name
                                </label>
                                <input
                                    id={`grandfather-middleName`}
                                    type="text"
                                    name="middleName"
                                    value={grandfatherData.middleName || ""}
                                    onChange={handleGrandfatherChange}
                                    autoComplete="off"
                                    onBlur={(e) => {
                                        // Trim the value when user leaves the field
                                        const trimmedValue = e.target.value.trim();
                                        if (trimmedValue !== e.target.value) {
                                            handleGrandfatherChange({
                                                target: { name: "middleName", value: trimmedValue },
                                            });
                                        }
                                    }}
                                />
                            </div>
                            
                            {/* Last Name */}
                            <div className="form-group addmember-form-group">
                                <label
                                    htmlFor={`grandfather-lastName`}
                                    className={'required-labelsss'}
                                >
                                    Last Name
                                    <span style={{ color: "red" }}> *</span>
                                </label>
                                <input
                                    id={`grandfather-lastName`}
                                    type="text"
                                    name="lastName"
                                    value={grandfatherData.lastName || ""}
                                    onChange={handleGrandfatherChange}
                                    onBlur={(e) => {
                                        // Trim the value when user leaves the field
                                        const trimmedValue = e.target.value.trim();
                                        if (trimmedValue !== e.target.value) {
                                            handleGrandfatherChange({
                                                target: { name: "lastName", value: trimmedValue },
                                            });
                                        }
                                        // Mark field as touched
                                        setTouchedFields((prev) => ({
                                            ...prev,
                                            primary: {
                                                ...prev.primary,
                                                lastName: true,
                                            },
                                        }));
                                    }}
                                    required={true}
                                />
                                {fieldValidationErrors.lastName &&
                                    (touchedFields.primary.lastName || formSubmitted) && (
                                        <div
                                            style={{
                                                color: "#d32f2f",
                                                fontSize: "12px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {fieldValidationErrors.lastName}
                                        </div>
                                    )}
                            </div>

                            {/* Suffix */}
                            <div className="form-group addmember-form-group">
                                <label
                                    htmlFor={`grandfather-suffix`}
                                    className={'required-labelsss'}
                                >
                                    Suffix
                                </label>
                                <input
                                    id={`grandfather-suffix`}
                                    type="text"
                                    name="suffix"
                                    autoComplete="off"
                                    value={(() => {
                                        // Display value with period appended at the end only (UI only)
                                        const suffixValue = grandfatherData.suffix || "";
                                        if (suffixValue === "") return "";
                                        // Remove ALL periods from the value, then add one at the end
                                        const cleanedValue = suffixValue.replace(/\./g, "");
                                        return cleanedValue + ".";
                                    })()}
                                    onChange={(e) => {
                                        // Remove ALL periods from input (user can type normally, periods will be removed)
                                        let inputValue = e.target.value;
                                        // Remove all periods (user shouldn't type periods, we add it automatically)
                                        inputValue = inputValue.replace(/\./g, "");
                                        
                                        // Update the stored value (without any periods)
                                        handleGrandfatherChange({
                                            target: { name: "suffix", value: inputValue },
                                        });
                                        
                                        // For suffix field, re-check matches when suffix changes
                                        if (isGeneration2 && grandfatherData.firstName) {
                                            const enteredFirstName = grandfatherData.firstName.trim().toLowerCase();
                                            const enteredSuffix = inputValue.trim().toLowerCase();
                                            const hasMatch = memoizedParentChildren.some(child => {
                                                const childFirstName = getChildFirstName(child).toLowerCase();
                                                const childSuffix = getChildSuffix(child);
                                                // In edit mode, exclude the current member being edited
                                                if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
                                                    return false;
                                                }
                                                // Check if first names match
                                                if (childFirstName !== enteredFirstName || childFirstName === "") {
                                                    return false;
                                                }
                                                // Check suffixes: require special initial if both no suffix or both same suffix
                                                const bothNoSuffix = enteredSuffix === "" && childSuffix === "";
                                                const bothSameSuffix = enteredSuffix !== "" && childSuffix !== "" && enteredSuffix === childSuffix;
                                                return bothNoSuffix || bothSameSuffix;
                                            });
                                            
                                            if (hasMatch) {
                                                // Mark specialName as touched so error shows immediately
                                                setTouchedFields((prev) => ({
                                                    ...prev,
                                                    primary: {
                                                        ...prev.primary,
                                                        specialName: true,
                                                    },
                                                }));
                                            }
                                        }
                                    }}
                                    onBlur={(e) => {
                                        // Trim the value when user leaves the field
                                        let trimmedValue = e.target.value.trim();
                                        // Remove ALL periods before storing (we add it only for display)
                                        trimmedValue = trimmedValue.replace(/\./g, "");
                                        
                                        if (trimmedValue !== grandfatherData.suffix) {
                                            handleGrandfatherChange({
                                                target: { name: "suffix", value: trimmedValue },
                                            });
                                        }
                                        // Validate suffix to check for matches when suffix changes
                                        if (isGeneration2 && grandfatherData.firstName) {
                                            const enteredFirstName = grandfatherData.firstName.trim().toLowerCase();
                                            const enteredSuffix = trimmedValue.toLowerCase();
                                            const hasMatch = memoizedParentChildren.some(child => {
                                                const childFirstName = getChildFirstName(child).toLowerCase();
                                                const childSuffix = getChildSuffix(child);
                                                // In edit mode, exclude the current member being edited
                                                if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
                                                    return false;
                                                }
                                                // Check if first names match
                                                if (childFirstName !== enteredFirstName || childFirstName === "") {
                                                    return false;
                                                }
                                                // Check suffixes: require special initial if both no suffix or both same suffix
                                                const bothNoSuffix = enteredSuffix === "" && childSuffix === "";
                                                const bothSameSuffix = enteredSuffix !== "" && childSuffix !== "" && enteredSuffix === childSuffix;
                                                return bothNoSuffix || bothSameSuffix;
                                            });
                                            
                                            if (hasMatch && !grandfatherData.specialName?.trim()) {
                                                // Mark specialName as touched so error shows immediately
                                                setTouchedFields((prev) => ({
                                                    ...prev,
                                                    primary: {
                                                        ...prev.primary,
                                                        specialName: true,
                                                    },
                                                }));
                                                setFieldValidationErrors((prev) => ({
                                                    ...prev,
                                                    specialName: "Special initial is required when first name matches an existing child.",
                                                }));
                                            } else if (!hasMatch) {
                                                setFieldValidationErrors((prev) => ({
                                                    ...prev,
                                                    specialName: "",
                                                }));
                                            }
                                        }
                                    }}
                                    disabled={grandfatherData.gender === "2" || grandfatherData.gender === 2}
                                    style={(grandfatherData.gender === "2" || grandfatherData.gender === 2) ? { backgroundColor: "#f5f5f5", opacity: 0.6, cursor: "not-allowed" } : {}}
                                />
                            </div>
                            
                            {/* Maiden Name */}
                            <div className="form-group addmember-form-group">
                                <label
                                    htmlFor={`grandfather-maidenName`}
                                    className="required-labelsss"
                                >
                                    Maiden Name
                                </label>
                                <input
                                    id={`grandfather-maidenName`}
                                    type="text"
                                    name="maidenName"
                                    value={grandfatherData.maidenName}
                                    onChange={handleGrandfatherChange}
                                    onBlur={(e) => {
                                        // Trim the value when user leaves the field
                                        const trimmedValue = e.target.value.trim();
                                        if (trimmedValue !== e.target.value) {
                                            handleGrandfatherChange({
                                                target: { name: "maidenName", value: trimmedValue },
                                            });
                                        }
                                    }}
                                    disabled={grandfatherData.gender === "1" || grandfatherData.gender === 1}
                                    style={(grandfatherData.gender === "1" || grandfatherData.gender === 1) ? { backgroundColor: "#f5f5f5", opacity: 0.6, cursor: "not-allowed" } : {}}
                                />
                            </div>
                        </div>

                        <div className="addmember-descendant-band addmember-descendant-band--lifecycle">
                            {/* Living / Deceased */}
                            <div id="alive-status-field" className="form-group addmember-form-group">
                                <label className="required-label">Living / Deceased</label>
                                <DropdownSelect
                                    options={sortedLivingDeceasedOptions}
                                    value={grandfatherData.aliveStatus}
                                    onChange={(val) => {
                                        handleGrandfatherChange({
                                            target: { name: "aliveStatus", value: val },
                                        });
                                        // Clear error when value changes
                                        if (val) {
                                            setFieldValidationErrors((prev) => ({
                                                ...prev,
                                                aliveStatus: "",
                                            }));
                                        }
                                        // If Deceased (2) is selected, clear and disable email
                                        // If Living (1) is selected, enable email and make it required
                                        if (val === "2") {
                                            // Deceased: clear email
                                            setGrandfatherData((prev) => ({
                                                ...prev,
                                                email: "",
                                            }));
                                            // Clear email validation error
                                            setFieldValidationErrors((prev) => ({
                                                ...prev,
                                                email: "",
                                            }));
                                        }
                                    }}
                                    onBlur={() => {
                                        setTouchedFields((prev) => ({
                                            ...prev,
                                            primary: {
                                                ...prev.primary,
                                                aliveStatus: true,
                                            },
                                        }));
                                    }}
                                    placeholder="Select"
                                    maxMenuHeight={95}
                                />
                                {fieldValidationErrors.aliveStatus &&
                                    (touchedFields.primary.aliveStatus || formSubmitted) && (
                                        <div
                                            style={{
                                                color: "#d32f2f",
                                                fontSize: "12px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {fieldValidationErrors.aliveStatus}
                                        </div>
                                    )}
                            </div>

                            {/* Date of Birth */}
                            <div className="form-group addmember-form-group">
                                <label className={isPrimaryMinor ? "" : "required-label"}>
                                    Date of Birth
                                </label>
                                <CustomDatePicker
                                    value={grandfatherData.dob}
                                    onChange={handleGrandfatherChange}
                                    name="dob"
                                    format={ADD_MEMBER_DATE_FORMAT}
                                    autoFocusNextOnComplete
                                    required={!isPrimaryMinor}
                                    error={
                                        !!dateValidationErrors.primary.dob &&
                                        (touchedFields.primary.dob || formSubmitted)
                                    }
                                    helperText={
                                        touchedFields.primary.dob || formSubmitted
                                            ? dateValidationErrors.primary.dob
                                            : ""
                                    }
                                />
                            </div>

                            <AddMemberAutoInput
                                label="Current Age"
                                name="currentAge"
                                value={grandfatherData.currentAge}
                            />

                            <div className="form-group addmember-form-group">
                                <label>Date of Death</label>
                                <CustomDatePicker
                                    value={grandfatherData.dod || ""}
                                    onChange={handleGrandfatherChange}
                                    name="dod"
                                    format={ADD_MEMBER_DATE_FORMAT}
                                    autoFocusNextOnComplete
                                    required={grandfatherData.aliveStatus == "2"}
                                    disabled={grandfatherData.aliveStatus !== "2"}
                                    error={
                                        !!dateValidationErrors.primary.dod &&
                                        (touchedFields.primary.dod || formSubmitted)
                                    }
                                    helperText={
                                        touchedFields.primary.dod || formSubmitted
                                            ? dateValidationErrors.primary.dod
                                            : ""
                                    }
                                />
                            </div>

                            <AddMemberAutoInput
                                label="Age at Time of Death"
                                name="ageOfDeath"
                                value={grandfatherData.ageOfDeath}
                            />

                            <AddMemberAutoInput
                                label="# of Years Deceased"
                                name="yearsDeceased"
                                value={yearsDeceased}
                            />
                        </div>

                        <div className="addmember-descendant-band addmember-descendant-band--extra">
                            {/* State of Birth */}
                            <div className="form-group addmember-form-group">
                                <label id="state-of-birth" className="required-label">U.S. State or Country of Birth</label>
                                <DropdownSelect
                                    options={sortedStates}
                                    value={grandfatherData.stateOfBirth}
                                    onChange={(val) => {
                                        handleGrandfatherChange({
                                            target: { name: "stateOfBirth", value: val },
                                        })
                                        if (val) {
                                            setFieldValidationErrors((prev) => ({
                                                ...prev,
                                                stateOfBirth: "",
                                            }));
                                        }
                                    }}
                                    placeholder="Select State where you were born"
                                    maxMenuHeight={180}
                                    required={true}
                                />
                                {fieldValidationErrors.stateOfBirth &&
                                    (touchedFields.primary.stateOfBirth || formSubmitted) && (
                                        <div
                                            style={{
                                                color: "#d32f2f",
                                                fontSize: "12px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {fieldValidationErrors.stateOfBirth}
                                        </div>
                                    )}
                            </div>
                            
                            {/* Current State Living */}
                            <div className="form-group addmember-form-group">
                                <label>Current U.S. State or Country Living</label>
                                <DropdownSelect
                                    options={sortedStates}
                                    value={grandfatherData.currentStateResiding}
                                    onChange={(val) =>
                                        handleGrandfatherChange({
                                            target: { name: "currentStateResiding", value: val },
                                        })
                                    }
                                    placeholder="Select your current state living"
                                    maxMenuHeight={180}
                                    disabled={grandfatherData.aliveStatus === "2"}
                                />
                            </div>

                            <div className="form-group addmember-form-group">
                                <label htmlFor="grandfather-nickName">Nick Name</label>
                                <input
                                    id="grandfather-nickName"
                                    type="text"
                                    name="nickName"
                                    value={grandfatherData.nickName}
                                    onChange={handleGrandfatherChange}
                                    onBlur={(e) => {
                                        const trimmedValue = e.target.value.trim();
                                        if (trimmedValue !== e.target.value) {
                                            handleGrandfatherChange({
                                                target: { name: "nickName", value: trimmedValue },
                                            });
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="addmember-descendant-band addmember-descendant-band--marital">
                            <div id="marital-status-field" className="form-group addmember-form-group">
                                <label className="required-label">Marital Status</label>
                                <DropdownSelect
                                    options={sortedMaritalStatusOptions}
                                    value={grandfatherData.maritalStatus}
                                    onChange={(val) => {
                                        handleGrandfatherChange({
                                            target: { name: "maritalStatus", value: val },
                                        });
                                        if (val) {
                                            setFieldValidationErrors((prev) => ({
                                                ...prev,
                                                maritalStatus: "",
                                            }));
                                        }
                                    }}
                                    onBlur={() => {
                                        setTouchedFields((prev) => ({
                                            ...prev,
                                            primary: {
                                                ...prev.primary,
                                                maritalStatus: true,
                                            },
                                        }));
                                    }}
                                    placeholder="Select your marital status"
                                    maxMenuHeight={180}
                                />
                                {fieldValidationErrors.maritalStatus &&
                                    (touchedFields.primary.maritalStatus || formSubmitted) && (
                                        <div
                                            style={{
                                                color: "#d32f2f",
                                                fontSize: "12px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {fieldValidationErrors.maritalStatus}
                                        </div>
                                    )}
                            </div>

                            {/* Multiple Birth Status */}
                            <div className="form-group addmember-form-group">
                                <label>Multiple Birth Status</label>
                                <DropdownSelect
                                    options={multipleBirthsOptions}
                                    value={grandfatherData.multiple_births_type}
                                    onChange={(val) => {
                                        handleGrandfatherChange({
                                            target: { name: "multiple_births_type", value: val },
                                        });
                                        // Clear multiple_birth_details and order when type changes (supports edit/downgrade: e.g. Triplet → Twin; user re-selects siblings)
                                        setGrandfatherData((prev) => ({
                                            ...prev,
                                            multiple_birth_details: [],
                                            multiple_birth_order: ""
                                        }));
                                        // Clear validation errors
                                        setFieldValidationErrors((prev) => ({
                                            ...prev,
                                            multipleBirthSiblings: "",
                                            multipleBirthOrders: "",
                                        }));
                                    }}
                                    placeholder="Multiple births"
                                    maxMenuHeight={88}
                                    disabled={currentGeneration === 1 || !shouldEnableMultipleBirths}
                                />
                            </div>
                            
                            <div className="form-group addmember-form-group">
                                <label
                                    className={requiredSiblingCount ? "required-label" : ""}
                                    style={{ marginBottom: "8px", display: "block" }}
                                >
                                    Select Sibling &amp; Assign Birth Order
                                    {requiredSiblingCount ? ` (Select ${requiredSiblingCount})` : ""}
                                </label>
                                {grandfatherData.multiple_births_type &&
                                shouldEnableMultipleBirths &&
                                siblingsWithSameDob.length > 0 ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            cursor: "pointer",
                                            height: "38px",
                                            padding: "0 12px",
                                            border: "1px solid #ccc",
                                            borderRadius: "4px",
                                            backgroundColor: "white",
                                            width: "100%",
                                            boxSizing: "border-box",
                                        }}
                                        onClick={() =>
                                            setIsSiblingOrderAccordionOpen(!isSiblingOrderAccordionOpen)
                                        }
                                    >
                                        <span
                                            style={{
                                                flex: 1,
                                                fontSize: "14px",
                                                color: selectedSiblingIds.length > 0 ? "#000" : "#999",
                                            }}
                                        >
                                            {selectedSiblingIds.length > 0
                                                ? `${selectedSiblingIds.length} sibling${selectedSiblingIds.length > 1 ? "s" : ""} selected`
                                                : "Click to select siblings"}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "18px",
                                                fontWeight: "bold",
                                                color: "#666",
                                                transition: "transform 0.2s",
                                                transform: isSiblingOrderAccordionOpen
                                                    ? "rotate(180deg)"
                                                    : "rotate(0deg)",
                                            }}
                                        >
                                            ▼
                                        </span>
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        readOnly
                                        disabled
                                        placeholder="Set multiple birth status first"
                                        style={{ backgroundColor: "#f5f5f5", opacity: 0.7 }}
                                    />
                                )}
                            </div>
                        </div>
                        
                        {/* Accordion Content for Sibling Selection */}
                        {grandfatherData.multiple_births_type && 
                         shouldEnableMultipleBirths && 
                         siblingsWithSameDob.length > 0 && 
                         isSiblingOrderAccordionOpen && (
                            <div className="sibling-accordion-content" style={{
                                maxHeight: "200px",
                                overflowY: "auto",
                                overflowX: "hidden",
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px",
                                padding: "4px 4px 4px 12px",
                                marginTop: "0px",
                                width: "100%",
                                gridColumn: "1 / -1"
                            }}>
                                <div className="row form-grid addmember-form-grid sibling-accordion-grid" style={{ width: "100%", gap: "0px" }}>
                                    {siblingsWithSameDob.map((sibling) => {
                                        const currentDetail = (grandfatherData.multiple_birth_details || []).find(
                                            d => d.family_member_id === sibling.value
                                        );
                                        const isSelected = !!currentDetail;
                                        const currentOrder = currentDetail?.order || "";
                                        
                                        // Count only other siblings (exclude current member in edit mode so deselecting doesn't disable re-select)
                                        const existingDetails = grandfatherData.multiple_birth_details || [];
                                        const currentMemberId = isEditMode && editMemberData ? (editMemberData.id?.toString() || editMemberData.family_member_id?.toString() || null) : null;
                                        const selectedCount = currentMemberId
                                            ? existingDetails.filter(d => (d.family_member_id?.toString() || "") !== currentMemberId).length
                                            : existingDetails.length;
                                        const isMaxReached = requiredSiblingCount && selectedCount >= requiredSiblingCount;
                                        const isCheckboxDisabled = !isSelected && isMaxReached;
                                        
                                        return (
                                            <React.Fragment key={sibling.value}>
                                                {/* Sibling Checkbox + Name */}
                                                <div className="form-group addmember-form-group">
                                                    <div style={{ 
                                                        display: "flex", 
                                                        alignItems: "center", 
                                                        gap: "6px",
                                                        width: "100%"
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={isCheckboxDisabled}
                                                            onChange={(e) => {
                                                                const isChecked = e.target.checked;
                                                                const existingDetails = grandfatherData.multiple_birth_details || [];
                                                                const currentMemberIdEdit = isEditMode && editMemberData ? (editMemberData.id?.toString() || editMemberData.family_member_id?.toString() || null) : null;
                                                                const otherSiblingsCount = currentMemberIdEdit
                                                                    ? existingDetails.filter(d => (d.family_member_id?.toString() || "") !== currentMemberIdEdit).length
                                                                    : existingDetails.length;
                                                                // Prevent selecting more than required (count other siblings only in edit mode)
                                                                if (isChecked && requiredSiblingCount && otherSiblingsCount >= requiredSiblingCount) {
                                                                    return;
                                                                }
                                                                
                                                                let updatedDetails;
                                                                if (isChecked) {
                                                                    // Add sibling (without order initially, user will select order)
                                                                    const existingIndex = existingDetails.findIndex(
                                                                        d => d.family_member_id === sibling.value
                                                                    );
                                                                    
                                                                    if (existingIndex < 0) {
                                                                        // Add new sibling
                                                                        updatedDetails = [
                                                                            ...existingDetails,
                                                                            {
                                                                                order: "",
                                                                                family_member_id: sibling.value
                                                                            }
                                                                        ];
                                                                    } else {
                                                                        updatedDetails = existingDetails;
                                                                    }
                                                                } else {
                                                                    // Remove sibling
                                                                    updatedDetails = existingDetails.filter(
                                                                        d => d.family_member_id !== sibling.value
                                                                    );
                                                                }
                                                                
                                                                setGrandfatherData((prev) => ({
                                                                    ...prev,
                                                                    multiple_birth_details: updatedDetails
                                                                }));
                                                                
                                                                // Validate orders
                                                                validateMultipleBirthOrders(updatedDetails, parseInt(grandfatherData.multiple_births_type, 10));
                                                                
                                                                // Mark field as touched
                                                                setTouchedFields((prev) => ({
                                                                    ...prev,
                                                                    primary: {
                                                                        ...prev.primary,
                                                                        multipleBirthSiblings: true,
                                                                        multipleBirthOrders: true,
                                                                    },
                                                                }));
                                                                
                                                                // Check selection count (excluding current member)
                                                                if (requiredSiblingCount) {
                                                                    const currentMemberId = isEditMode && editMemberData ? (
                                                                        editMemberData.id?.toString() || 
                                                                        editMemberData.family_member_id?.toString() || 
                                                                        null
                                                                    ) : null;
                                                                    const selectedSiblingsCount = updatedDetails.filter(detail => {
                                                                        // Exclude current member from count
                                                                        const detailId = detail.family_member_id?.toString() || "";
                                                                        if (currentMemberId && detailId && detailId === currentMemberId) {
                                                                            return false;
                                                                        }
                                                                        return true;
                                                                    }).length;
                                                                    
                                                                    // Always update error state based on validation
                                                                    setFieldValidationErrors((prev) => {
                                                                        if (selectedSiblingsCount === requiredSiblingCount) {
                                                                            // Clear error if validation passes
                                                                            if (!prev.multipleBirthSiblings) return prev;
                                                                            return {
                                                                                ...prev,
                                                                                multipleBirthSiblings: "",
                                                                            };
                                                                        } else {
                                                                            // Set error if validation fails
                                                                            const errorMessage = `Please select exactly ${requiredSiblingCount} sibling${requiredSiblingCount > 1 ? 's' : ''} with the same DOB (or within ±3 days)`;
                                                                            if (prev.multipleBirthSiblings === errorMessage) return prev;
                                                                            return {
                                                                                ...prev,
                                                                                multipleBirthSiblings: errorMessage,
                                                                            };
                                                                        }
                                                                    });
                                                                }
                                                            }}
                                                            style={{
                                                                width: "18px",
                                                                height: "18px",
                                                                cursor: isCheckboxDisabled ? "not-allowed" : "pointer",
                                                                opacity: isCheckboxDisabled ? 0.5 : 1
                                                            }}
                                                        />
                                                        <label style={{ 
                                                            fontSize: "14px",
                                                            margin: 0,
                                                            cursor: isCheckboxDisabled ? "not-allowed" : "pointer"
                                                        }}>
                                                            {sibling.label || "Unknown"}
                                                        </label>
                                                    </div>
                                                </div>
                                                
                                                {/* Order Dropdown - Always shown to ensure 2 fields per row */}
                                                <div className="form-group addmember-form-group">
                                                    {isSelected ? (
                                                        <DropdownSelect
                                                            options={orderOptions}
                                                            value={currentOrder ? currentOrder.toString() : ""}
                                                            onChange={(val) => {
                                                                const newOrder = val || "";
                                                                const existingDetails = grandfatherData.multiple_birth_details || [];
                                                                
                                                                // Update the order for this specific sibling
                                                                const updatedDetails = existingDetails.map((detail) => {
                                                                    if (detail.family_member_id === sibling.value) {
                                                                        return {
                                                                            ...detail,
                                                                            order: newOrder ? parseInt(newOrder, 10) : ""
                                                                        };
                                                                    }
                                                                    return detail;
                                                                });
                                                                
                                                                setGrandfatherData((prev) => ({
                                                                    ...prev,
                                                                    multiple_birth_details: updatedDetails
                                                                }));
                                                                
                                                                // Validate orders
                                                                validateMultipleBirthOrders(updatedDetails, parseInt(grandfatherData.multiple_births_type, 10));
                                                                
                                                                // Mark field as touched
                                                                setTouchedFields((prev) => ({
                                                                    ...prev,
                                                                    primary: {
                                                                        ...prev.primary,
                                                                        multipleBirthOrders: true,
                                                                    },
                                                                }));
                                                                
                                                                // Check selection count (excluding current member)
                                                                if (requiredSiblingCount) {
                                                                    const currentMemberId = isEditMode && editMemberData?.id ? editMemberData.id.toString() : null;
                                                                    const selectedSiblingsCount = updatedDetails.filter(detail => {
                                                                        // Exclude current member from count
                                                                        // Compare as strings to handle UUIDs and numbers
                                                                        const detailId = detail.family_member_id?.toString() || "";
                                                                        if (currentMemberId && detailId === currentMemberId) {
                                                                            return false;
                                                                        }
                                                                        return true;
                                                                    }).length;
                                                                    
                                                                    // Always clear error if count matches, or set error if it doesn't
                                                                    setFieldValidationErrors((prev) => {
                                                                        if (selectedSiblingsCount === requiredSiblingCount) {
                                                                            // Clear error if validation passes
                                                                            if (!prev.multipleBirthSiblings) return prev;
                                                                            return {
                                                                                ...prev,
                                                                                multipleBirthSiblings: "",
                                                                            };
                                                                        } else {
                                                                            // Set error if validation fails
                                                                            const errorMessage = `Please select exactly ${requiredSiblingCount} sibling${requiredSiblingCount > 1 ? 's' : ''} with the same DOB (or within ±3 days)`;
                                                                            if (prev.multipleBirthSiblings === errorMessage) return prev;
                                                                            return {
                                                                                ...prev,
                                                                                multipleBirthSiblings: errorMessage,
                                                                            };
                                                                        }
                                                                    });
                                                                }
                                                            }}
                                                            placeholder="Select order"
                                                            maxMenuHeight={150}
                                                        />
                                                    ) : (
                                                        <div style={{ height: "0", visibility: "hidden", margin: 0, padding: 0 }}></div>
                                                    )}
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                                
                                {/* Current Member Order Input - Always in a 2-field row */}
                                <div className="row form-grid addmember-form-grid sibling-accordion-grid" style={{ 
                                    marginTop: "8px",
                                    paddingTop: "8px",
                                    borderTop: "1px solid #e0e0e0",
                                    width: "100%",
                                    gap: "0px"
                                }}>
                                    <div className="form-group addmember-form-group">
                                        <label style={{ 
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            margin: 0
                                        }}>
                                            {grandfatherData.firstName || "Current Member"}
                                        </label>
                                    </div>
                                    <div className="form-group addmember-form-group">
                                        <DropdownSelect
                                            options={orderOptions}
                                            value={grandfatherData.multiple_birth_order ? grandfatherData.multiple_birth_order.toString() : ""}
                                            onChange={(val) => {
                                                const newOrder = val || "";
                                                handleGrandfatherChange({
                                                    target: { name: "multiple_birth_order", value: newOrder ? parseInt(newOrder, 10) : "" },
                                                });
                                                
                                                // Validate orders including current member
                                                const allDetails = grandfatherData.multiple_birth_details || [];
                                                validateMultipleBirthOrdersWithCurrentMember(
                                                    allDetails, 
                                                    newOrder || "", 
                                                    parseInt(grandfatherData.multiple_births_type, 10)
                                                );
                                                
                                                // Mark field as touched
                                                setTouchedFields((prev) => ({
                                                    ...prev,
                                                    primary: {
                                                        ...prev.primary,
                                                        multipleBirthOrders: true,
                                                    },
                                                }));
                                            }}
                                            placeholder="Select order"
                                            maxMenuHeight={150}
                                        />
                                    </div>
                                </div>
                                
                                {fieldValidationErrors.multipleBirthOrders &&
                                 (touchedFields.primary.multipleBirthOrders || formSubmitted) && (
                                    <div
                                        style={{
                                            color: "#d32f2f",
                                            fontSize: "12px",
                                            marginTop: "8px",
                                        }}
                                    >
                                        {fieldValidationErrors.multipleBirthOrders}
                                    </div>
                                )}
                                
                                {fieldValidationErrors.multipleBirthSiblings &&
                                 (touchedFields.primary.multipleBirthSiblings || formSubmitted) && (
                                    <div
                                        style={{
                                            color: "#d32f2f",
                                            fontSize: "12px",
                                            marginTop: "4px",
                                        }}
                                    >
                                        {fieldValidationErrors.multipleBirthSiblings}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="addmember-descendant-band addmember-descendant-band--parents">
                                <div className="form-group addmember-form-group bio-descendant-parent-field">
                                    <label className="required-label">
                                        Bio Descendant Parent(s)
                                    </label>
                                    <input
                                        type="text"
                                        name="bio_descendant_parent"
                                        value={grandfatherData.bio_descendant_parent}
                                        required={true}
                                        placeholder="Put both parent names if child of the Top/Start of Line, otherwise 1 parent input"
                                        onChange={(e) =>
                                            setGrandfatherData((prev) => ({
                                                ...prev,
                                                bio_descendant_parent: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="form-group addmember-form-group">
                                    <label>Bio Non-Descendant Parent</label>
                                    <input
                                        type="text"
                                        name="bio_non_descendant_parent"
                                        value={grandfatherData.bio_non_descendant_parent}
                                        onChange={(e) =>
                                            setGrandfatherData((prev) => ({
                                                ...prev,
                                                bio_non_descendant_parent: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="form-group addmember-form-group">
                                    <label>Step Parent</label>
                                    <input
                                        type="text"
                                        name="step_parent"
                                        value={grandfatherData.step_parent}
                                        onChange={(e) =>
                                            setGrandfatherData((prev) => ({
                                                ...prev,
                                                step_parent: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="form-group addmember-form-group">
                                    <label>
                                        Adopted Parent<span style={{ textTransform: "lowercase" }}>(s)</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="adopted_parent"
                                        value={grandfatherData.adopted_parent}
                                        placeholder="Put both parent names"
                                        onChange={(e) =>
                                            setGrandfatherData((prev) => ({
                                                ...prev,
                                                adopted_parent: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                        </div>

                        <div className="addmember-descendant-band addmember-descendant-band--relations">
                            <AddMemberAutoRelationField
                                label="Relation to Top of the Line"
                                name="relation"
                                value={
                                    isEditMode
                                        ? editMemberData?.generation_level === 1 ||
                                          editMemberData?.userData?.generation_level === 1
                                            ? "Self"
                                            : normalizeRelation(
                                                  grandfatherData.relation ||
                                                      editMemberData?.relation ||
                                                      ""
                                              )
                                        : primaryRelation === "Self" || primaryRelationName === "Self"
                                          ? "Self"
                                          : normalizeRelation(grandfatherData?.relation || "")
                                }
                            />
                            <AddMemberAutoRelationField
                                label="Relation to Head of Line"
                                name="relation_to_head_of_line"
                                value={(() => {
                                    const relation =
                                        editMemberData?.relation_to_head_of_line ||
                                        grandfatherData.relation_to_head_of_line ||
                                        "";
                                    const isGeneration1 =
                                        editMemberData?.generation_level === 1 ||
                                        editMemberData?.userData?.generation_level === 1;
                                    if (isGeneration1) return "Self";
                                    let cleaned = relation.replace(/\s+of\s+head$/i, "").trim();
                                    cleaned = cleaned.replace(/\s*\(head\s+of\s+line\)$/i, "").trim();
                                    if (
                                        cleaned.match(/^self\s*\(head\s+of\s+line\)$/i) ||
                                        relation.match(/^self\s*\(head\s+of\s+line\)$/i)
                                    ) {
                                        return "Self";
                                    }
                                    return normalizeRelation(cleaned);
                                })()}
                            />
                        </div>

                        <div className="addmember-descendant-band addmember-descendant-band--contact">
                                <div className="form-group addmember-form-group">
                                    <label>Cell Type</label>
                                    <DropdownSelect
                                        options={sortedCellTypeOptions}
                                        value={grandfatherData.phone_type}
                                        onChange={(val) =>
                                            handleGrandfatherChange({
                                                target: { name: "phone_type", value: val },
                                            })
                                        }
                                        placeholder="Select cell type"
                                        maxMenuHeight={88}
                                        disabled={grandfatherData.aliveStatus === "2"}
                                    />
                                </div>
                                <div className="form-group addmember-form-group">
                                    <label>Country Code &amp; Cell Phone #</label>
                                    <PhoneInput
                                        country={"us"}
                                        value={getPhoneInputValue(
                                            grandfatherData.phone,
                                            grandfatherData.countryCode
                                        )}
                                        placeholder=""
                                        {...phoneInputCountrySearchProps}
                                        onChange={(value, country) =>
                                            handlePhoneChange(value, country, false)
                                        }
                                        disabled={grandfatherData.aliveStatus === "2"}
                                        inputStyle={{
                                            width: "100%",
                                            height: "47px",
                                            minHeight: "47px",
                                            paddingLeft: "48px",
                                            backgroundColor: grandfatherData.aliveStatus === "2" ? "#f5f5f5" : "white",
                                            opacity: grandfatherData.aliveStatus === "2" ? 0.6 : 1,
                                            cursor: grandfatherData.aliveStatus === "2" ? "not-allowed" : "text",
                                        }}
                                        containerStyle={{
                                            marginBottom: "0",
                                            width: "100%",
                                            maxWidth: "100%",
                                        }}
                                        inputProps={{
                                            name: "phone",
                                        }}
                                    />
                                </div>

                                <div className="form-group addmember-form-group">
                                    {(() => {
                                        return (
                                            <>
                                                <label
                                                    className={
                                                        isPrimaryEmailRequired ? "required-label" : ""
                                                    }
                                                >
                                                    Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={grandfatherData.email || ""}
                                                    placeholder="name@example.com"
                                                    onChange={(e) => {
                                                        const nextEmail = e.target.value;
                                                        setGrandfatherData((prev) => ({
                                                            ...prev,
                                                            email: nextEmail,
                                                        }));
                                                        setFieldValidationErrors((prev) => ({
                                                            ...prev,
                                                            email: "",
                                                        }));
                                                        if (emailCheckTimeoutRef?.current) {
                                                            clearTimeout(
                                                                emailCheckTimeoutRef.current
                                                            );
                                                        }
                                                        const trimmed = nextEmail.trim();
                                                        if (trimmed) {
                                                            emailCheckTimeoutRef.current =
                                                                setTimeout(() => {
                                                                    const formatError =
                                                                        getEmailFormatError(
                                                                            trimmed
                                                                        );
                                                                    if (formatError) {
                                                                        setFieldValidationErrors(
                                                                            (prev) => ({
                                                                                ...prev,
                                                                                email: formatError,
                                                                            })
                                                                        );
                                                                        return;
                                                                    }
                                                                    if (
                                                                        grandfatherData.aliveStatus ===
                                                                        "1"
                                                                    ) {
                                                                        checkEmailExists(
                                                                            trimmed,
                                                                            "primary"
                                                                        );
                                                                    }
                                                                }, 500);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        if (emailCheckTimeoutRef?.current) {
                                                            clearTimeout(
                                                                emailCheckTimeoutRef.current
                                                            );
                                                        }
                                                        const trimmed = e.target.value.trim();
                                                        if (trimmed !== e.target.value) {
                                                            setGrandfatherData((prev) => ({
                                                                ...prev,
                                                                email: trimmed,
                                                            }));
                                                        }
                                                        const formatError =
                                                            getEmailFormatError(trimmed);
                                                        if (formatError) {
                                                            setFieldValidationErrors((prev) => ({
                                                                ...prev,
                                                                email: formatError,
                                                            }));
                                                        } else if (
                                                            trimmed &&
                                                            grandfatherData.aliveStatus === "1"
                                                        ) {
                                                            checkEmailExists(trimmed, "primary");
                                                        }
                                                        setTouchedFields((prev) => ({
                                                            ...prev,
                                                            primary: {
                                                                ...prev.primary,
                                                                email: true,
                                                            },
                                                        }));
                                                    }}
                                                    required={isPrimaryEmailRequired}
                                                    disabled={grandfatherData.aliveStatus === "2"}
                                                    className="form-control"
                                                    style={{ width: "100%", boxSizing: "border-box" }}
                                                />
                                            </>
                                        );
                                    })()}
                                    {fieldValidationErrors.email &&
                                        (touchedFields.primary.email || formSubmitted) && (
                                            <div
                                                style={{
                                                    color: "#d32f2f",
                                                    fontSize: "12px",
                                                    marginTop: "4px",
                                                }}
                                            >
                                                {fieldValidationErrors.email}
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>

                        <div className="row form-grid addmember-form-grid">
                            {/* Special Notes */}
                            <div className="form-group addmember-form-group addmember-notes-stretch" style={{ gridColumn: "1 / -1" }}>
                                <label>Special Notes</label>
                                <AddMemberSpecialNotesField
                                    name="note"
                                    value={grandfatherData.note}
                                    onChange={handleGrandfatherChange}
                                />
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    );
}